import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { I18NextService } from 'angular-i18next';
import moment, { Moment } from 'moment';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import { UpdateTaskDto } from '../../dtos/update-task-dto.interface';
import { SortDirection } from '../../enums/sort-direction.enum';
import { TaskSortingProperties } from '../../enums/task-sorting-properties.enum';
import { TaskState } from '../../enums/task-state.enum';
import { WorkflowArea } from '../../enums/workflow-area.enum';
import { TaskMetaInformation } from '../../interfaces/task-meta-information.interface';

import { SubjectType } from '../../enums/subject-type.enum';
@Component({
    selector: 'pm-tasks-overview-table',
    templateUrl: './pm-tasks-overview-table.component.html',
    styleUrls: ['./pm-tasks-overview-table.component.scss'],
})
export class PmTasksOverviewTableComponent implements OnChanges {
    @Input() data: TaskMetaInformation[] = [];

    @Input() assigneeOptions: INameId[] = [];
    @Input() anyActiveFilter: boolean = false;
    @Input() activeSortBy: TaskSortingProperties | null = null;
    @Input() activeSortOrder: SortDirection | null = null;

    // Selection state from parent (Single Source of Truth)
    @Input() selectedTaskIds: Set<string> = new Set<string>();
    @Input() selectAllToggled: boolean = false;

    @Output() updateTask = new EventEmitter<{ taskId: string; updateTaskDto: UpdateTaskDto }>();
    @Output() sortTasks = new EventEmitter<{
        sortBy: TaskSortingProperties | null;
        sortOrder: SortDirection | null;
    }>();
    @Output() addProject = new EventEmitter<void>();
    @Output() onTaskClicked = new EventEmitter<{
        projectId: string;
        area: WorkflowArea;
        sectionName: string;
        /** REV-22155 — PERSON: the task belongs to a person project. */
        subjectType?: SubjectType;
    }>();

    @Output() toggleSelectAll = new EventEmitter<void>();
    @Output() toggleSelectTask = new EventEmitter<TaskMetaInformation>();

    TaskSortingProperties = TaskSortingProperties;

    statusOptions: any[] = [];

    tasksStatesFormArray: UntypedFormArray;

    // Range selection properties
    private lastSelectedTaskIndex: number | null = null;

    // Flyout properties
    openedStatusFlyoutTaskId: string | null = null;
    openedAssigneeFlyoutTaskId: string | null = null;
    openedDueDateFlyoutTaskId: string | null = null;

    flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };
    calendarSelectedDate: Moment | null = null;

    constructor(private readonly i18NextService: I18NextService) {
        this.statusOptions = [
            { value: TaskState.OPEN, label: this.i18NextService.t('aumico_pm_tasks_status_open') as string },
            {
                value: TaskState.IN_PROGRESS,
                label: this.i18NextService.t('aumico_pm_tasks_status_in_progress') as string,
            },
            {
                value: TaskState.IN_REVIEW,
                label: this.i18NextService.t('aumico_pm_tasks_status_in_review') as string,
            },
            { value: TaskState.DONE, label: this.i18NextService.t('aumico_pm_tasks_status_done') as string },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data?.length) {
            this.tasksStatesFormArray = this.getAllTasksStatesAsFormArray();
        }
    }

    private getAllTasksStatesAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = this.data.map((task) =>
            this.addFormGroup({ taskId: task.id, taskState: task.taskState }),
        );
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(taskState): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(taskState).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(taskState[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    sortBy(sortBy: TaskSortingProperties): void {
        if (this.activeSortBy === sortBy) {
            if (this.activeSortOrder === SortDirection.ASC) {
                this.sortTasks.emit({ sortBy, sortOrder: SortDirection.DESC });
            } else {
                this.sortTasks.emit({ sortBy: null, sortOrder: null });
        }
        } else {
            this.sortTasks.emit({ sortBy, sortOrder: SortDirection.ASC });

        }
    }

    // EDITING ASSIGNEE FUNCTIONS

    getInitials(name: string): string {
        if (!name) return '';
        if (name === this.i18NextService.t('aumico_tasks_unassigned')) {
            return '-';
        }
        const parts = name.split(' ');
        return parts
            .map((p) => p[0])
            .join('')
            .toUpperCase();
    }

    openAssigneeFlyout(taskId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();

        this.flyoutPosition = {
            top: rect.bottom,
            right: rect.right,
            left: rect.left,
        };

        this.openedStatusFlyoutTaskId = null;
        this.openedAssigneeFlyoutTaskId = taskId;
    }

    closeAssigneeFlyout(): void {
        this.openedAssigneeFlyoutTaskId = null;
    }

    updateAssignee(task: TaskMetaInformation, newAssignee: string | null): void {
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { assignedTo: newAssignee } });
        this.closeAssigneeFlyout();
    }

    // EDITING DUE DATE FUNCTIONS

    openDueDateFlyout(taskId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const flyoutHeight = 360;
        const viewportHeight = window.innerHeight;

        let top = rect.bottom;
        if (rect.bottom + flyoutHeight > viewportHeight) {
            top = rect.top - flyoutHeight;
        }

        this.flyoutPosition = {
            top,
            right: rect.right,
            left: rect.left,
        };

        this.openedStatusFlyoutTaskId = null;
        this.openedAssigneeFlyoutTaskId = null;
        this.openedDueDateFlyoutTaskId = taskId;
        const task = this.data.find((t) => t.id === taskId);
        const taskDueDate = task?.dueDate ? new Date(task.dueDate) : null;
        this.calendarSelectedDate = taskDueDate ? moment.utc(taskDueDate).startOf('day') : null;
    }

    closeDueDateFlyout(): void {
        this.openedDueDateFlyoutTaskId = null;
        this.calendarSelectedDate = null;
    }

    selectToday(task: TaskMetaInformation): void {
        const today = new Date();
        const normalized = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0));
        this.calendarSelectedDate = moment.utc(normalized).startOf('day');
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { dueDate: normalized } });
        this.closeDueDateFlyout();
    }

    clearDate(_task: TaskMetaInformation): void {}

    updateDueDate(task: TaskMetaInformation, newDueDate: Moment): void {
        if (!newDueDate) return;

        const y = newDueDate.year();
        const m = newDueDate.month();
        const d = newDueDate.date();

        const asUtc = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { dueDate: asUtc } });
        this.calendarSelectedDate = moment.utc(asUtc).startOf('day');
        this.closeDueDateFlyout();
    }

    isOverdue(dueDate: string | Date): boolean {
        return new Date(dueDate) < new Date();
    }

    isDueSoon(dueDate: string | Date): boolean {
        const today = new Date();
        const due = new Date(dueDate);

        const timeDiff = due.getTime() - today.getTime();
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

        return daysDiff >= 0 && daysDiff <= 7;
    }

    // EDITING TASK STATE FUNCTIONS

    getStatusLabel(status: TaskState): string {
        return this.statusOptions.find((opt) => opt.value === status)?.label ?? 'Select';
    }

    openStatusFlyout(taskId: string, buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;

        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();

        this.flyoutPosition = {
            top: rect.bottom,
            right: rect.right,
            left: rect.left,
        };

        this.openedAssigneeFlyoutTaskId = null;
        this.openedStatusFlyoutTaskId = taskId;
    }

    closeStatusFlyout(): void {
        this.openedStatusFlyoutTaskId = null;
    }

    updateStatus(task: TaskMetaInformation, newStatus: TaskState): void {
        this.updateTask.emit({ taskId: task.id, updateTaskDto: { taskState: newStatus } });
        this.closeStatusFlyout();
    }

    onAddProject(): void {
        this.addProject.emit();
    }

    /**
     * Latest comments arrive with mention tokens in the `@[name|id]` format
     * (same convention as AnnotationsComponent.formatContentForView). Render
     * the user-facing name bold so it's visually distinct in the cell.
     * Consumed via [innerHTML] so Angular's default sanitizer keeps <b>
     * and strips anything unsafe in the comment body.
     */
    formatLatestComment(content: string | null): string {
        if (!content) return '';
        return content.replace(/@\[([^|\]]+)\|([^\]]+)\]/g, (_match, name) => `<b>@${name}</b>`);
    }

    onTaskClick(projectId: string, area: WorkflowArea, sectionName: string, subjectType?: SubjectType): void {
        this.onTaskClicked.emit({ projectId, area, sectionName, subjectType });
    }

    toggleCheckAll() {
        this.toggleSelectAll.emit();
    }

    toggleCheckTask(task: TaskMetaInformation, event?: MouseEvent) {
        if (event?.shiftKey && this.lastSelectedTaskIndex !== null) {
            this.handleShiftClick(task);
        } else {
            this.handleNormalClick(task);
        }
    }

    onTaskCheckboxClick(task: TaskMetaInformation, event: MouseEvent): void {
        event.stopPropagation();

        if (event.shiftKey && this.lastSelectedTaskIndex !== null) {
            this.handleShiftClick(task);
        } else {
            this.handleNormalClick(task);
        }
    }

    private handleShiftClick(clickedTask: TaskMetaInformation): void {
        const clickedIndex = this.data.findIndex((t) => t.id === clickedTask.id);

        if (clickedIndex === -1 || this.lastSelectedTaskIndex === null) {
            this.handleNormalClick(clickedTask);
            return;
        }

        const startIndex = Math.min(this.lastSelectedTaskIndex, clickedIndex);
        const endIndex = Math.max(this.lastSelectedTaskIndex, clickedIndex);

        this.lastSelectedTaskIndex = clickedIndex;

        const tasksInRange = this.data.slice(startIndex, endIndex + 1);

        const isClickedTaskSelected = this.selectedTaskIds.has(clickedTask.id);

        const tasksToToggle = tasksInRange.filter((task) => {
            const isTaskSelected = this.selectedTaskIds.has(task.id);
            if (isClickedTaskSelected) {
                return isTaskSelected;
            } else {
                return !isTaskSelected;
            }
        });

        tasksToToggle.forEach((task) => {
            this.toggleSelectTask.emit(task);
        });
    }

    private handleNormalClick(task: TaskMetaInformation): void {
        this.lastSelectedTaskIndex = this.data.findIndex((t) => t.id === task.id);
        this.toggleSelectTask.emit(task);
    }
}
